import { Icon, type IconName } from '@smartech/ui';

interface CustomButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'simple' | 'error' | 'link';
  icon?: IconName;
  iconComponent?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  size?: 'sm' | 'md' | 'lg';
}

const CustomButton: React.FC<CustomButtonProps> = ({
  children,
  variant = 'primary',
  icon,
  iconComponent,
  iconPosition = 'left',
  onClick,
  disabled = false,
  loading = false,
  className = '',
  type = 'button',
  size = 'md',
}) => {
  const baseClasses =
    'btn flex items-center text-nowrap gap-sm rounded-lg px-2xl py-lg text-sm transition-colors font-semibold justify-center';
  const disabledClasses = disabled ? 'opacity-50' : '';

  const sizeClasses = {
    sm: 'py-sm',
    md: 'py-md',
    lg: 'py-lg',
  };

  const variantClasses = {
    primary:
      'bg-button-bg-primary text-button-fg-primary hover:bg-brand-700 disabled:hover:bg-bg-disabled disabled:text-fg-disabled disabled:border disabled:border-border-disabled_subtle disabled:bg-bg-tertiary',
    secondary:
      'bg-button-bg-secondary text-button-fg-secondary border border-button-border-secondary hover:bg-brand-50 disabled:hover:bg-button-bg-secondary disabled:text-fg-disabled disabled:border disabled:border-border-disabled_subtle disabled:bg-button-bg-secondary',
    simple:
      'border border-border-primary bg-button-secondary-bg text-button-secondary-fg border-border-primary text-fg-secondary bg-button-bg-secondary hover:bg-bg-secondary disabled:hover:bg-button-bg-secondary',
    tertiary:
      'bg-transparent text-button-fg-tertiary hover:bg-brand-50 disabled:hover:bg-transparent',
    error:
      'bg-button-error-primary text-button-fg-primary disabled:hover:bg-bg-disabled disabled:text-fg-disabled disabled:border disabled:border-border-disabled_subtle disabled:bg-bg-tertiary',
    link: 'bg-transparent text-button-fg-secondary !px-0',
  };

  const iconBaseClasses = `text-current text-xl ${disabled ? '!text-fg-disabled' : ''}`;

  const iconClasses = {
    primary: 'text-button-fg-primary',
    secondary: 'text-button-fg-secondary',
    simple: 'text-text-primary-600',
  };

  // Spinner component
  const Spinner = () => (
    <svg
      className="animate-spin text-xl"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      width="20"
      height="20"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading || disabled) return;
    onClick?.(e);
  };

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses[variant]} ${iconClasses} ${sizeClasses[size]} ${disabledClasses} ${loading ? 'cursor-auto' : ''} ${className}`}
    >
      {loading && <Spinner />}
      {!loading && iconComponent && iconComponent}
      {!loading && icon && iconPosition === 'right' && (
        <Icon
          name={icon}
          className={`${iconBaseClasses} ${iconClasses[variant as keyof typeof iconClasses] || ''}`}
        />
      )}
      {children}
      {!loading && icon && iconPosition === 'left' && (
        <Icon
          name={icon}
          className={`${iconBaseClasses} ${iconClasses[variant as keyof typeof iconClasses] || ''}`}
        />
      )}
    </button>
  );
};

export default CustomButton;
